import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { titles } from "@/mocks/titles";
import { news } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { getHotTitles } from "@/lib/titles";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import TitleCard from "@/components/feature/TitleCard";
import NewsCard from "@/components/feature/NewsCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntries from "@/pages/home/components/CategoryEntries";

export default function Home() {
  useSeo({
    title: "WW我的快乐在哪里 - 最新影视资讯与剧情介绍",
    description:
      "WW我的快乐在哪里网站提供丰富的影视资讯、剧情介绍及影评分析，帮助用户全面了解影视作品内容与动态，涵盖电影和电视剧多种类别。",
    keywords: "WW我的快乐在哪里,影视资讯,剧情介绍,电影推荐,电视剧全集,影视评论,观看指南",
    path: "https://rozhryj.cn/",
  });

  const slides = titles.filter((item) => item.backdrop);
  const latestNews = [...news].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);
  const hotTitles = getHotTitles(6);
  const topSpecials = specials.slice(0, 3);

  return (
    <>
      <HeroCarousel slides={slides} />

      <section className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
        <Reveal>
          <SectionHeading
            title="快捷分类入口"
            subtitle="按类型快速进入，电影、电视剧、综艺一网打尽"
          />
          <CategoryEntries />
        </Reveal>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
          <Reveal>
            <SectionHeading
              title="最新影视资讯"
              subtitle="按发布时间倒序更新，第一时间了解行业动态与作品消息"
              moreTo="/special"
              moreLabel="更多专题"
            />
          </Reveal>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {latestNews.map((item, index) => (
              <Reveal key={item.id} delay={index * 60}>
                <NewsCard item={item} className="h-full" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
        <Reveal>
          <SectionHeading
            title="热门剧情介绍推荐"
            subtitle="当前关注度较高的电影与电视剧，先看简介再决定看哪部"
            moreTo="/search"
            moreLabel="搜索更多"
          />
        </Reveal>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {hotTitles.map((item, index) => (
            <Reveal key={item.id} delay={index * 50}>
              <TitleCard item={item} className="h-full" />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
          <Reveal>
            <SectionHeading
              title="精选专题合集"
              subtitle="按主题打包的片单，解决你的选择困难"
              moreTo="/special"
              moreLabel="查看全部专题"
            />
          </Reveal>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {topSpecials.map((special, index) => (
              <Reveal key={special.id} delay={index * 70}>
                <Link
                  to={`/special/${special.id}`}
                  className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-200">
                    <img
                      src={special.cover}
                      alt={`${special.name} 专题主图`}
                      title={`${special.name} - WW我的快乐在哪里专题`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
                      {special.tag}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                      {special.name}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{special.intro}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-secondary-600">
                      共 {special.titleIds.length} 部作品
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
        <Reveal>
          <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200 bg-gradient-to-br from-primary-50 to-background-100 p-6 md:grid-cols-2 md:p-10">
            <div>
              <h2 className="text-2xl font-black text-foreground-950 md:text-3xl">
                关于 WW我的快乐在哪里
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                我们是一个专注影视资讯与剧情介绍的门户网站，为影视爱好者整理最新动态、剧情解析与影评推荐。
                无论你想找一部下饭剧，还是想深入了解某个作品的来龙去脉，都能在这里找到线索。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap"
                >
                  了解我们
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
                <Link
                  to="/search"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
                >
                  <i className="ri-search-line" aria-hidden="true" />
                  搜索影视
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: `${titles.length}+`, label: "收录作品" },
                { value: `${specials.length}+`, label: "精选专题" },
                { value: `${news.length}+`, label: "资讯条目" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 text-center"
                >
                  <p className="text-2xl font-black text-primary-600 md:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}